import type { FC } from "react"
import { useState } from "react"

import { AxisBottom, AxisLeft } from "@visx/axis"
import { GridRows } from "@visx/grid"
import { Group } from "@visx/group"
import { ParentSize } from "@visx/responsive"
import { scaleLinear } from "@visx/scale"
import { AreaClosed, LinePath } from "@visx/shape"

import {
  AXIS_COLOR,
  CONSUMPTION_COLOR,
  GRID_COLOR,
  PRODUCTION_COLOR,
  axisLabelProps,
  formatHour,
  formatKw,
} from "./chart-theme"

const MARGIN = { top: 16, right: 90, bottom: 32, left: 44 }

export type ProductionOverlaySeries = {
  consumptionKw: number[]
  productionKw: number[]
}

type ChartProps = {
  height: number
  series: ProductionOverlaySeries
  width: number
}

const Chart: FC<ChartProps> = ({ height, series, width }) => {
  const [hoverHour, setHoverHour] = useState<number | null>(null)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = height - MARGIN.top - MARGIN.bottom
  if (innerWidth <= 0 || innerHeight <= 0) return null

  const maxKw = Math.max(0.1, ...series.consumptionKw, ...series.productionKw)
  const xScale = scaleLinear({ domain: [0, 23], range: [0, innerWidth] })
  const yScale = scaleLinear({
    domain: [0, maxKw * 1.15],
    nice: true,
    range: [innerHeight, 0],
  })

  return (
    <div className="relative">
      <svg
        height={height}
        role="img"
        aria-label="Average day: solar production versus household consumption"
        width={width}
        onMouseLeave={() => setHoverHour(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          const x = event.clientX - rect.left - MARGIN.left
          setHoverHour(Math.max(0, Math.min(23, Math.round(xScale.invert(x)))))
        }}
      >
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridRows
            height={innerHeight}
            scale={yScale}
            stroke={GRID_COLOR}
            width={innerWidth}
          />
          <AreaClosed<number>
            data={series.productionKw}
            fill={PRODUCTION_COLOR}
            fillOpacity={0.18}
            x={(_, hour) => xScale(hour) ?? 0}
            y={(value) => yScale(value) ?? 0}
            yScale={yScale}
          />
          <LinePath<number>
            data={series.productionKw}
            stroke={PRODUCTION_COLOR}
            strokeWidth={2}
            x={(_, hour) => xScale(hour) ?? 0}
            y={(value) => yScale(value) ?? 0}
          />
          <LinePath<number>
            data={series.consumptionKw}
            stroke={CONSUMPTION_COLOR}
            strokeWidth={2}
            x={(_, hour) => xScale(hour) ?? 0}
            y={(value) => yScale(value) ?? 0}
          />
          <text
            fill={PRODUCTION_COLOR}
            fontFamily="Barlow, sans-serif"
            fontSize={11}
            fontWeight={700}
            x={innerWidth + 6}
            y={(yScale(series.productionKw[23]) ?? 0) + 4}
          >
            Solar output
          </text>
          <text
            fill={CONSUMPTION_COLOR}
            fontFamily="Barlow, sans-serif"
            fontSize={11}
            fontWeight={700}
            x={innerWidth + 6}
            y={(yScale(series.consumptionKw[23]) ?? 0) + 4}
          >
            Home usage
          </text>
          {hoverHour !== null ? (
            <g>
              <line
                stroke={AXIS_COLOR}
                strokeDasharray="3,3"
                x1={xScale(hoverHour) ?? 0}
                x2={xScale(hoverHour) ?? 0}
                y1={0}
                y2={innerHeight}
              />
              <circle
                cx={xScale(hoverHour) ?? 0}
                cy={yScale(series.consumptionKw[hoverHour]) ?? 0}
                fill={CONSUMPTION_COLOR}
                r={4}
                stroke="#ffffff"
                strokeWidth={2}
              />
              <circle
                cx={xScale(hoverHour) ?? 0}
                cy={yScale(series.productionKw[hoverHour]) ?? 0}
                fill={PRODUCTION_COLOR}
                r={4}
                stroke="#ffffff"
                strokeWidth={2}
              />
            </g>
          ) : null}
          <AxisBottom
            scale={xScale}
            stroke={AXIS_COLOR}
            tickFormat={(value) => formatHour(Number(value))}
            tickLabelProps={() => ({ ...axisLabelProps, textAnchor: `middle` })}
            tickStroke={AXIS_COLOR}
            tickValues={[0, 4, 8, 12, 16, 20, 23]}
            top={innerHeight}
          />
          <AxisLeft
            scale={yScale}
            stroke={AXIS_COLOR}
            tickFormat={(value) => `${Number(value)} kW`}
            tickLabelProps={() => ({
              ...axisLabelProps,
              dx: `-0.25em`,
              dy: `0.25em`,
              textAnchor: `end`,
            })}
            tickStroke={AXIS_COLOR}
          />
        </Group>
      </svg>
      {hoverHour !== null ? (
        <div className="pointer-events-none absolute top-2 left-14 rounded-lg border border-border bg-background px-3 py-2 shadow-md">
          <p className="font-display text-xs font-bold text-foreground">
            {formatHour(hoverHour)}
          </p>
          <p className="mt-0.5 text-xs text-muted">
            Usage:{` `}
            <span className="font-semibold tabular-nums text-foreground">
              {formatKw(series.consumptionKw[hoverHour])}
            </span>
          </p>
          <p className="text-xs text-muted">
            Solar:{` `}
            <span className="font-semibold tabular-nums text-foreground">
              {formatKw(series.productionKw[hoverHour])}
            </span>
          </p>
        </div>
      ) : null}
    </div>
  )
}

export const ProductionOverlayChart: FC<ProductionOverlaySeries> = ({
  consumptionKw,
  productionKw,
}) => (
  <div className="h-[280px]">
    <ParentSize>
      {({ height, width }) => (
        <Chart
          height={height}
          series={{ consumptionKw, productionKw }}
          width={width}
        />
      )}
    </ParentSize>
  </div>
)
